<template>
  <ion-page>
    <ion-content :fullscreen="true">
      <LoadingState />
    </ion-content>
  </ion-page>
</template>

<script>
import { REMOTE_ASSETS } from '@/constants/REMOTE_ASSETS';
import LoadingState from '@/components/common/LoadingState.vue';
import { IonContent, IonPage, isPlatform, useIonRouter } from '@ionic/vue';
import { mapState } from 'vuex';
import ONBOARDING_STATUS from '@/constants/ONBOARDING_STATUS';
import {
  AUTHENTICATED_HOME_PATH,
  ONBOARDING_PATH,
  replaceWithRootNavigation,
  resolvePostGateNavigation,
} from '@/utils/post-login-navigation';
import { clearPendingDeepLinkRoute, getPendingDeepLinkRoute } from '@/utils/deeplink';
import {
  requestDirectoryAccess,
  removeDirectoryWithSAF,
  downloadImagesWithSAF,
  downloadImagesToLocalStorage,
} from '@/utils/storage-access-framework';
// merged imports above

export default {
  name: 'LoadingPage',
  components: {
    IonContent,
    IonPage,
    LoadingState,
  },
  setup() {
    const ionRouter = useIonRouter();
    return { ionRouter };
  },

  computed: {
    ...mapState('homepage', ['middleNavIcons', 'featuredCards', 'justForYou']),
    ...mapState('updates', ['unreadCount']),
    ...mapState('profile', ['hasRegistered']),
    ...mapState('churchUpdates', ['featuredChurchUpdates']),
  },

  methods: {
    getPendingDeepLinkRoute() {
      return getPendingDeepLinkRoute();
    },
    async navigateAfterPreload() {
      if (this.$route.path !== '/pre-loading') return;

      const targetDecision = resolvePostGateNavigation({
        hasRegistered: this.hasRegistered,
        onboardingCompleteStatus: ONBOARDING_STATUS.STAGE_3_LIFESTAGE_COMPLETED,
        pendingDeepLinkRoute: this.getPendingDeepLinkRoute(),
        fallbackTarget: AUTHENTICATED_HOME_PATH,
      });

      const isRootTarget =
        targetDecision.target === AUTHENTICATED_HOME_PATH ||
        targetDecision.target === ONBOARDING_PATH;
      const navigation = isRootTarget
        ? replaceWithRootNavigation(this.$router, this.ionRouter, targetDecision.target)
        : this.$router.push(targetDecision.target);

      await navigation.catch(error => {
        console.error('[PreLoading] Failed to navigate after login:', error);
        throw error;
      });

      if (targetDecision.shouldConsumePendingDeepLink) {
        clearPendingDeepLinkRoute();
      }
    },
    async preloadAssets() {
      // Preload assets so Home doesn't fetch on mount
      const animations = this.$store.state.homepage.allAnimations || [];
      const activeAnimation = animations.find(a => a.isActive === true);
      const isScratch = !!(
        activeAnimation &&
        activeAnimation.type &&
        activeAnimation.type.length > 0 &&
        activeAnimation.type[0].__component &&
        activeAnimation.type[0].__component.includes('scratch')
      );
      const isGachapon = !!(
        (activeAnimation && activeAnimation.name === 'gachapon') ||
        (activeAnimation &&
          activeAnimation.type &&
          activeAnimation.type.length > 0 &&
          activeAnimation.type[0].__component &&
          activeAnimation.type[0].__component.includes('gachapon')) ||
        (activeAnimation && (!activeAnimation.type || activeAnimation.type.length === 0))
      );

      if (isScratch) {
        const animationPath = activeAnimation.type[0];
        try {
          if (isPlatform('android')) {
            const safAccess = await requestDirectoryAccess();
            if (safAccess.granted) {
              await removeDirectoryWithSAF(animationPath);
              const ok = await downloadImagesWithSAF(animationPath);
              if (!ok) {
                await downloadImagesToLocalStorage(animationPath);
              }
              localStorage.setItem('filePermissionGranted', ok ? 'true' : 'false');
            } else {
              await downloadImagesToLocalStorage(animationPath);
              localStorage.setItem('filePermissionGranted', 'false');
            }
          } else {
            // iOS / web fallback: use localStorage prefetch
            await downloadImagesToLocalStorage(animationPath);
            localStorage.setItem('filePermissionGranted', 'false');
          }
        } catch (e) {
          console.warn('[PreLoading] Scratch asset preload failed, using localStorage fallback', e);
          await downloadImagesToLocalStorage(animationPath);
          localStorage.setItem('filePermissionGranted', 'false');
        }
      } else {
        // Clear flag if not using scratch
        localStorage.removeItem('filePermissionGranted');
      }

      // For gachapon, preload static machine assets into browser cache
      if (isGachapon) {
        const preload = src =>
          new Promise(resolve => {
            const img = new Image();
            img.onload = img.onerror = () => resolve();
            img.src = src;
          });
        const gachaponAssets = [
          REMOTE_ASSETS.gachaponTop,
          REMOTE_ASSETS.gachaponBottom,
          '/assets/images/gachapon-knob.png',
          '/assets/images/ball.webp',
        ];
        try {
          await Promise.all(gachaponAssets.map(preload));
        } catch (_) {
          // ignore; best-effort preload
        }
      }
    },
  },

  async mounted() {
    console.log('[PreLoading] Page mounted');

    // Check if data already exists (loaded by App.vue)
    const hasMiddleNavIcons =
      this.$store.state.homepage?.middleNavIcons &&
      Array.isArray(this.$store.state.homepage.middleNavIcons) &&
      this.$store.state.homepage.middleNavIcons.length > 0;
    const hasFeaturedCards =
      this.$store.state.homepage?.featuredCards &&
      Array.isArray(this.$store.state.homepage.featuredCards) &&
      this.$store.state.homepage.featuredCards.length > 0;
    const hasAnimations =
      this.$store.state.homepage?.allAnimations &&
      Array.isArray(this.$store.state.homepage.allAnimations) &&
      this.$store.state.homepage.allAnimations.length > 0;
    const hasOnboardStatus = this.$store.state.profile?.hasRegistered !== undefined;

    // If critical data already exists, skip loading and redirect immediately
    // No need to preload assets since data is already available
    if (hasMiddleNavIcons && hasFeaturedCards && hasAnimations && hasOnboardStatus) {
      console.log(
        '[PreLoading] Data already loaded by App.vue, skipping reload and redirecting immediately',
      );
      // Redirect immediately without any delay
      this.$nextTick(() => {
        this.navigateAfterPreload();
      });
      return;
    }

    console.log('[PreLoading] Data not found, loading from API...');
    try {
      // Load data with timeout to prevent indefinite hanging
      const loadStartTime = Date.now();
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => {
          reject(new Error('Pre-loading timeout after 15 seconds'));
        }, 15000);
      });

      const loadPromise = (async () => {
        // Use non-blocking check - load from cache first, sync in background
        await this.$store.dispatch('profile/getOnboardStatus', {
          useCache: true,
          backgroundSync: true,
        });

        // Load other data in parallel (like App.vue does)
        await Promise.allSettled([
          this.$store.dispatch('homepage/getMiddleNavIcons'),
          this.$store.dispatch('homepage/getFeaturedCards'),
          this.$store.dispatch('homepage/getJustForYou'),
          this.$store.dispatch('updates/getUpdates', 'all'),
          this.$store.dispatch('homepage/getAllAnimations'),
          this.$store.dispatch('churchUpdates/getFeaturedChurchUpdates'),
        ]);
      })();

      await Promise.race([loadPromise, timeoutPromise]);

      const loadDuration = Date.now() - loadStartTime;
      console.log(`[PreLoading] Data loaded successfully in ${loadDuration}ms`);

      // Redirect immediately after data is loaded - don't wait for asset preloading
      // Asset preloading can happen in the background and won't block navigation
      setTimeout(() => {
        this.navigateAfterPreload();
      }, 0);

      // Preload assets in the background (non-blocking)
      // This happens after redirect, so it doesn't delay navigation
      this.preloadAssets().catch(err => {
        console.warn('[PreLoading] Asset preload failed (non-critical):', err);
      });
    } catch (error) {
      console.error('[PreLoading] Error loading data:', error);
      // Even if loading fails or times out, redirect to home
      // The homepage can handle missing data gracefully
      setTimeout(() => {
        this.navigateAfterPreload();
      }, 0);
    }
  },
};
</script>

<style scoped>
ion-content {
  --ion-background-color: #f2efed;
}
</style>
